export type SubscriptionReminderAction =
  | 'preview'
  | 'run'
  | 'test'
  | 'send_now'
  | 'save_settings';

export interface SubscriptionReminderSettings {
  enabled: boolean;
  templateAlias: string;
  reminderDays: number[];
  lookaheadDays: number;
  updatedAt: string;
}

export interface SubscriptionReminderAgendaEntry {
  type: 'renewal' | 'reminder';
  date: string;
  subscriptionId: string;
  subscriptionNumber: string;
  clientName: string;
  serviceName: string;
  amount: number;
  currencyCode: string;
  daysUntil: number;
  daysBefore?: number;
  reminderTargetDate?: string;
}

export interface SubscriptionReminderLogEntry {
  id: string;
  subscriptionId: string;
  subscriptionNumber: string;
  clientName: string;
  clientEmail: string;
  serviceName: string;
  targetDate: string;
  daysBefore: number;
  templateAlias: string;
  action: string;
  status: string;
  messageId: string;
  error: string;
  sentAt: string;
  source: string;
}

export interface SubscriptionReminderStats {
  totalSubscriptions: number;
  recurringSubscriptions: number;
  dueSoonCount: number;
  monthlyReminderDay: number;
  dueForReminderDayCount: number;
  dueInTenDaysCount: number;
  dueInThreeDaysCount: number;
  dueTodayCount: number;
  overdueCount: number;
  mandatePendingCount: number;
  atRiskCount: number;
  remindersDueTodayCount: number;
}

export interface SubscriptionReminderOverview {
  settings: SubscriptionReminderSettings;
  stats: SubscriptionReminderStats;
  agenda: SubscriptionReminderAgendaEntry[];
  recentLog: SubscriptionReminderLogEntry[];
  lastSentBySubscription: Record<string, SubscriptionReminderLogEntry>;
  siteUrl: string;
}

export interface SubscriptionReminderResult {
  status: string;
  subscriptionNumber?: string;
  recipientEmail?: string;
  daysBefore?: number;
  targetDate?: string;
  templateAlias?: string;
  messageId?: string;
  error?: string;
}

export interface SubscriptionReminderReport {
  dryRun: boolean;
  templateAlias: string;
  reminderDays: number[];
  stats: {
    checked: number;
    eligible: number;
    sent: number;
    failed: number;
    skippedAlreadySent: number;
    skippedNoEmail: number;
  };
  results: SubscriptionReminderResult[];
}

export interface SubscriptionReminderActionResponse {
  ok: boolean;
  action: SubscriptionReminderAction | string;
  overview?: SubscriptionReminderOverview;
  report?: SubscriptionReminderReport;
  result?: {
    templateAlias: string;
    recipientEmail: string;
    subscriptionNumber: string;
    messageId: string;
  };
  settings?: SubscriptionReminderSettings;
}

const ENDPOINT = '/api/admin/subscription-reminders';

const parseResponse = async <T,>(response: Response): Promise<T> => {
  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    throw new Error(payload?.message || payload?.error || 'Erreur rappels abonnement');
  }

  return payload as T;
};

export const adminSubscriptionReminderService = {
  async getOverview(): Promise<SubscriptionReminderOverview> {
    const response = await fetch(ENDPOINT, {
      credentials: 'include',
      cache: 'no-store',
    });

    const payload = await parseResponse<{ ok: true; overview: SubscriptionReminderOverview }>(
      response
    );
    return payload.overview;
  },

  async runAction(
    action: SubscriptionReminderAction | string,
    payload: Record<string, unknown> = {}
  ): Promise<SubscriptionReminderActionResponse> {
    const response = await fetch(ENDPOINT, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action,
        ...payload,
      }),
    });

    return parseResponse<SubscriptionReminderActionResponse>(response);
  },

  async saveSettings(
    settings: Partial<SubscriptionReminderSettings>
  ): Promise<SubscriptionReminderActionResponse> {
    const response = await fetch(ENDPOINT, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ settings }),
    });

    return parseResponse<SubscriptionReminderActionResponse>(response);
  },
};
